/* eslint-disable camelcase */
import ufeApi from 'services/api/ufeApi';
import { AUTH_HEADERS } from 'constants/authentication';
import UUIDv4 from 'utils/UUID';
import Hashing from 'utils/Hashing';
import RCPSCookies from 'utils/RCPSCookies';
import authenticationUtils from 'utils/Authentication';

/**
 * External Authentication API Service
 * Handles OAuth2.0 authentication flow for third-party integrations (e.g., ChatGPT)
 */

/**
 * External Auth Login
 * POST to /gway/v1/dotcom/auth/oauth2/login
 * Authenticates user with email/password and returns session info for external auth flow
 * @param {Object} params - Authentication parameters including OAuth2 protocol fields
 * @param {string} params.email - User email
 * @param {string} params.password - User password
 * @param {string} params.response_type - OAuth2 response type (e.g., 'code')
 * @param {string} params.client_id - OAuth2 client ID
 * @param {string} params.redirect_uri - OAuth2 redirect URI
 * @param {string} params.scope - OAuth2 scopes (space-separated)
 * @param {string} params.state - OAuth2 state parameter
 * @param {string} params.code_challenge - PKCE code challenge
 * @param {string} params.code_challenge_method - PKCE code challenge method (e.g., 'S256')
 * @param {string} params.nonce - Optional nonce parameter
 * @param {string} params.prompt - Optional prompt parameter
 * @param {string} params.display - Optional display parameter
 * @param {string} params.max_age - Optional max age parameter
 * @param {string} params.ui_locales - Optional UI locales parameter
 * @param {string} params.id_token_hint - Optional ID token hint
 * @param {string} params.login_hint - Optional login hint
 * @param {string} params.acr_values - Optional ACR values
 * @returns {Promise<Object>} Response with session_id, userId, message, token, hasIdentity
 */
async function externalAuthLogin(params) {
    const url = '/gway/v1/dotcom/auth/integration/login';

    const randomNumber = UUIDv4();
    const digest = await Hashing.sha256(randomNumber);
    const headers = { ...AUTH_HEADERS };

    if (RCPSCookies.isRCPSAuthEnabled()) {
        headers.deviceId = await authenticationUtils.getFingerPrint();
    }

    return ufeApi
        .makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({ ...params, randomNumber: params.email ? digest : '' }),
            headers
        })
        .then(data => {
            if (data?.errors) {
                return Promise.reject(data.errors[0]);
            }

            if (data?.errorCode) {
                return Promise.reject(data);
            }

            // Expected response structure:
            // {
            //   session_id: "5A8710E075DC0C1DB41D9D20D8123C47",
            //   userId: "simon3@qa4.com",
            //   message: "Login successful",
            // }
            return data;
        });
}

/**
 * Submit Grant Approval/Denial
 * POST /gway/v1/dotcom/auth/oauth2/grant
 * Submits the user's decision to approve or deny the external auth authorization request
 * @param {string} sessionId - Session ID from externalAuthLogin response
 * @param {boolean} approved - Whether user approved or denied authorization
 * @returns {Promise<Object>} Response with redirectUrl to redirect user back to third party
 */
async function submitGrant(sessionId, approved) {
    const url = '/gway/v1/dotcom/auth/integration/grant';

    const body = {
        session_id: sessionId,
        approved: approved
    };

    return ufeApi
        .makeRequest(url, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: AUTH_HEADERS
        })
        .then(data => {
            if (data?.errors) {
                return Promise.reject(data.errors[0]);
            }

            if (data?.errorCode) {
                return Promise.reject(data);
            }

            // Expected response structure:
            // {
            //   redirectUrl: "https://chatgpt.com/connector_platform_oauth_redirect?code=..."
            // }
            return data;
        });
}

export default {
    externalAuthLogin,
    submitGrant
};
